import { Link } from "react-router-dom";

const IMG = {
  transcript: "/examples/mfm-10k-to-1m/previews/transcript.jpg",
  blog: "/examples/mfm-10k-to-1m/previews/blog-post.jpg",
  showNotes: "/examples/mfm-10k-to-1m/previews/show-notes.jpg",
  discovery: "/examples/mfm-10k-to-1m/previews/discovery-titles.jpg",
  youtube: "/examples/mfm-10k-to-1m/previews/youtube.jpg",
  quotes: "/examples/mfm-10k-to-1m/previews/quotes.jpg",
  cover: "/examples/mfm-10k-to-1m/previews/cover.jpg",
};

const Figure = ({ src, alt, caption }) => (
  <figure className="my-10">
    <div className="rounded-xl overflow-hidden bg-[#0A0913] border border-neutral-800">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto" />
    </div>
    {caption && (
      <figcaption className="mt-3 text-sm text-neutral-500 italic text-center">
        {caption}
      </figcaption>
    )}
  </figure>
);

const Pullquote = ({ children }) => (
  <blockquote className="my-10 border-l-2 border-purple-500/60 pl-6 py-2 text-xl md:text-2xl font-unbounded font-semibold text-white leading-snug">
    {children}
  </blockquote>
);

const CTABlock = ({ title, body, href, label }) => (
  <aside className="my-12 rounded-2xl border border-purple-500/30 bg-gradient-to-b from-[#15131F] to-[#0E0D17] p-6 md:p-8">
    <p className="text-purple-300 uppercase text-xs tracking-widest font-semibold mb-3">
      Try it on your own podcast
    </p>
    <h3 className="text-2xl md:text-3xl font-unbounded font-semibold mb-3">
      {title}
    </h3>
    <p className="text-neutral-400 mb-5 leading-relaxed">{body}</p>
    <Link
      to={href}
      className="btn-primary inline-flex items-center gap-2 text-sm font-semibold"
    >
      {label} →
    </Link>
  </aside>
);

const PLATFORMS = [
  { name: "TikTok", body: "Fast hook in the first 2 seconds. Native language, curiosity gap, pattern interrupt. Captions carry the message even with sound off." },
  { name: "Instagram Reels", body: "Visual clarity + concise caption. Shareability is the primary ranking signal. Cover frame + text overlay matter most." },
  { name: "YouTube Shorts", body: "Search-friendly hook that mirrors the long-form title. Direct connection back to the full episode via description and pinned comment." },
  { name: "LinkedIn", body: "Insight-first framing. Professional voice. First line hook that stops the scroll before the 'see more' cutoff." },
  { name: "X / Twitter", body: "Sharp quote or a threaded takeaway. One idea per post. The strongest quotes stand alone; the rest belong in threads." },
];

const PlatformGrid = () => (
  <div className="my-12 rounded-2xl border border-neutral-800 bg-gradient-to-b from-[#15131F] to-[#0E0D17] p-6 md:p-8">
    <p className="text-purple-300 uppercase text-xs tracking-widest font-semibold mb-2">
      Platform adaptation
    </p>
    <p className="text-white font-unbounded font-semibold text-xl mb-6">
      Same clip, five surface-tuned rewrites
    </p>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {PLATFORMS.map((p, i) => (
        <div
          key={p.name}
          className="rounded-xl border border-neutral-800 bg-[#0A0913] p-4 hover:border-purple-500/30 transition-colors"
        >
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[10px] text-purple-300 uppercase tracking-widest font-semibold">
              {String(i + 1).padStart(2, "0")}
            </span>
            <span className="text-[10px] text-neutral-500 uppercase tracking-widest">
              Platform
            </span>
          </div>
          <p className="text-white font-semibold mb-1.5 leading-snug">{p.name}</p>
          <p className="text-xs text-neutral-400 leading-relaxed">{p.body}</p>
        </div>
      ))}
    </div>
  </div>
);

const Body = () => (
  <>
    <p className="lead">
      A podcast episode is not just a Spotify upload. It&apos;s five to
      ten short-form moments waiting to be published as separate
      discovery assets. Most podcasters post one launch-day
      announcement and call it social distribution. That leaves the
      entire top-of-funnel unused. Every episode contains multiple
      hooks, quotes, and angles that can become surface-tuned Shorts,
      Reels, TikToks, LinkedIn posts, and X posts — pulling new
      audiences back to the full episode from platforms Spotify and
      Apple can&apos;t reach.
    </p>
    <p>
      This guide is the operating playbook: what to clip, how to write
      hooks, how to adapt one idea across platforms, and the weekly
      workflow that makes it repeatable. It pairs directly with the
      broader{" "}
      <Link to="/blog/podcast-content-marketing" className="link">
        podcast content marketing system
      </Link>{" "}
      — social is one channel inside the larger growth graph.
    </p>

    <h2 id="clips-matter">Why podcast clips matter in 2026</h2>
    <p>
      Attention is fragmented and long-form is a commitment. A new
      listener won&apos;t choose a 90-minute episode on a stranger&apos;s
      recommendation. They&apos;ll watch a 45-second clip that ends on
      a compelling idea and click through when the hook lands.
    </p>
    <ul>
      <li>
        <strong>Clips are low-friction discovery.</strong> A first-time
        viewer can evaluate the show in under a minute — and clip
        performance is a leading indicator of long-form growth.
      </li>
      <li>
        <strong>Full episodes are hard to sample.</strong> No one
        auditions a podcast at 60 minutes. They audition at 45 seconds.
      </li>
      <li>
        <strong>Social platforms reward repeated, native formats.</strong>{" "}
        Consistent short-form output on TikTok, Reels, and Shorts
        compounds channel-level signals in the same way that consistent
        long-form output compounds YouTube&apos;s algorithm — the
        parallel to{" "}
        <Link to="/blog/youtube-seo-for-podcasters" className="link">
          YouTube SEO for podcasters
        </Link>{" "}
        is exact.
      </li>
      <li>
        <strong>Short-form is where the algorithm still surprises.</strong>{" "}
        A single clip can catch a recommendation wave and drive
        thousands of listens to the full episode. Long-form
        distribution doesn&apos;t have that kind of upside variance.
      </li>
      <li>
        <strong>Clips extend the episode&apos;s half-life.</strong>{" "}
        Each one is a discovery surface with its own decay curve —
        together they keep the episode active for weeks after publish.
      </li>
    </ul>
    <Pullquote>
      Nobody auditions a podcast at 60 minutes. They audition at 45
      seconds.
    </Pullquote>

    <h2 id="what-it-means">What &quot;podcast to social media&quot; really means</h2>
    <p>
      Not dumping a full episode link on every platform with a caption
      that says &quot;new episode is live.&quot; That&apos;s the
      default failure mode. Podcast-to-social means extracting the
      clip-worthy moments and repackaging them as native, surface-tuned
      content.
    </p>
    <ul>
      <li><strong>Strong hooks</strong> — the first 2-3 seconds that stop the scroll.</li>
      <li><strong>Quotes</strong> — memorable lines that stand alone in text or graphics.</li>
      <li><strong>Takeaways</strong> — bulletable ideas ready for LinkedIn or a thread.</li>
      <li><strong>Short ideas</strong> — one-concept-per-post material for X and short reels.</li>
      <li><strong>Social captions</strong> — platform-native copy that reframes the same idea.</li>
      <li><strong>Platform-specific angles</strong> — the same moment written differently for TikTok, Reels, Shorts, LinkedIn, X.</li>
      <li><strong>Teaser posts</strong> — pre- and post-launch content that primes the audience for the episode.</li>
    </ul>
    <p>
      This is the same repurposing logic covered in the{" "}
      <Link to="/blog/podcast-content-marketing" className="link">
        podcast content marketing hub
      </Link>{" "}
      — applied specifically to the short-form and social layer.
    </p>

    <h2 id="multiple-assets">Why one episode should create multiple short-form assets</h2>
    <p>
      A 90-minute episode contains four to six clip-worthy moments and
      ten to twenty quotable lines. Publishing one clip per episode
      leaves the rest of that content latent.
    </p>
    <ul>
      <li>
        <strong>Each episode has multiple angles.</strong> A single
        conversation can be positioned as a business insight, a
        personal story, a tactical framework, and a contrarian
        opinion — each one a different clip for a different audience
        segment.
      </li>
      <li>
        <strong>Different clips reach different segments.</strong>{" "}
        The tactical clip converts on LinkedIn; the surprising-opinion
        clip converts on TikTok; the emotional moment converts on
        Reels. Same episode, three different audiences.
      </li>
      <li>
        <strong>One idea works differently across platforms.</strong>{" "}
        The same 40-second moment needs three separate captions to
        perform on TikTok, Reels, and Shorts — and separate rewrites
        on LinkedIn and X.
      </li>
      <li>
        <strong>Repetition builds recognition.</strong> The same idea
        surfacing across three or four clips over two weeks anchors
        the show&apos;s positioning in the audience&apos;s head.
      </li>
      <li>
        <strong>More surfaces = more discovery chances.</strong> One
        upload → ten short-form entry points. Some will hit, most
        won&apos;t — but the ones that do compound the entire channel.
      </li>
    </ul>
    <p>
      The broader 12-asset repurposing framework is in{" "}
      <Link to="/blog/podcast-content-repurposing" className="link">
        how to turn one episode into 12 content assets
      </Link>
      . Social clips are the highest-leverage subset of that framework
      because their reach ceiling isn&apos;t capped by domain authority.
    </p>

    <h2 id="best-moments">The best podcast moments to turn into clips</h2>
    <p>
      Not every 45-second window is a clip. The moments that reliably
      perform share a small set of patterns. When you review an
      episode, look specifically for these:
    </p>
    <ul>
      <li>
        <strong>Surprising opinions.</strong> Anything that contradicts
        conventional wisdom in the guest&apos;s field. Curiosity
        compounds engagement.
      </li>
      <li>
        <strong>Emotional moments.</strong> A pause, a laugh, a
        vulnerable admission. Emotion is the strongest short-form
        signal.
      </li>
      <li>
        <strong>Contrarian takes.</strong> &quot;Everyone tells you X.
        Here&apos;s why X is wrong.&quot; Tension-first framing.
      </li>
      <li>
        <strong>Clear lessons.</strong> A single, portable insight the
        viewer can use tomorrow.
      </li>
      <li>
        <strong>Guest stories.</strong> Specific, high-detail
        narratives — a founder&apos;s $50K → $500K month, a moment of
        failure, a decision under pressure.
      </li>
      <li>
        <strong>Tactical frameworks.</strong> Numbered steps, named
        principles, memorable structures — the kind of content that
        performs on LinkedIn and travels back to the episode.
      </li>
      <li>
        <strong>Mistakes and myths.</strong> &quot;This is what most
        people get wrong…&quot; Correcting a common belief is a
        reliable hook format.
      </li>
      <li>
        <strong>Before / after moments.</strong> A specific transformation
        with concrete numbers or outcomes.
      </li>
      <li>
        <strong>Questions that create curiosity.</strong> The setup
        that makes viewers watch to the answer.
      </li>
    </ul>
    <Figure
      src={IMG.quotes}
      alt="Pull-quote highlights from a podcast episode — the source of clip-worthy moments and social captions."
      caption="Pull-quotes flag the clip-worthy moments — the source for hooks, captions, and short-form angles."
    />

    <h2 id="hooks">How to write Shorts hooks from podcast episodes</h2>
    <p>
      The hook is the whole game. The first 2-3 seconds of a Short
      determine 90% of retention. Every format below is a repeatable
      pattern grounded in what the episode actually delivers — not
      clickbait, because the episode has to earn the promise.
    </p>
    <ul>
      <li>
        <strong>&quot;Most podcasters get this wrong…&quot;</strong>{" "}
        Sets up correction. Works when the episode contains a specific,
        counterintuitive insight.
      </li>
      <li>
        <strong>&quot;The mistake nobody talks about…&quot;</strong>{" "}
        Curiosity gap format. Best paired with a tactical clip.
      </li>
      <li>
        <strong>&quot;Here&apos;s why your podcast is not growing…&quot;</strong>{" "}
        Problem-agitate format. Works when the audience is podcasters.
      </li>
      <li>
        <strong>&quot;This one idea changed how we think about…&quot;</strong>{" "}
        Reframing format. Best for framework clips.
      </li>
      <li>
        <strong>&quot;If you record podcasts, stop doing this…&quot;</strong>{" "}
        Direct-address pattern-interrupt. High CTR when the specific
        &quot;this&quot; is named in the first 3 seconds.
      </li>
    </ul>
    <p>
      A hook is not clickbait if the clip delivers what it promises.
      The failure mode is not too much curiosity — it&apos;s hooks that
      set up a payoff the episode doesn&apos;t actually contain. That
      erodes retention across the entire channel. The mechanics of
      title and hook engineering carry over from{" "}
      <Link to="/blog/youtube-seo-for-podcasters" className="link">
        YouTube SEO for podcasters
      </Link>{" "}
      — same logic applied to a shorter format.
    </p>

    <h2 id="platform-adaptation">How to adapt clips for TikTok, Reels, YouTube Shorts, and LinkedIn</h2>
    <p>
      Same clip. Five separate rewrites. Each surface rewards different
      signals — copy-pasting the same caption across platforms
      underperforms every time.
    </p>
    <PlatformGrid />
    <p>
      The clip itself can be the same 45 seconds. The framing —
      caption, on-screen text, cover frame, hashtags — should be
      re-engineered per platform. Two extra minutes per surface is the
      difference between a Reel that hits 800 views and one that hits
      80,000.
    </p>

    <h2 id="captions-quotes">How captions and quotes support social distribution</h2>
    <p>
      Even the strongest video clip needs text to convert. Captions
      provide context before the click. Quotes create standalone
      shareable assets that don&apos;t require the viewer to press
      play at all.
    </p>
    <ul>
      <li>
        <strong>Captions carry the sound-off version.</strong> On
        TikTok and Reels, roughly 80% of viewers watch with sound off
        for the first few seconds. Text overlay + caption decides
        retention.
      </li>
      <li>
        <strong>Quotes create standalone assets.</strong> A single
        strong line becomes a text tweet, a LinkedIn insight post, a
        Story graphic, and a pull-quote image — none of which require
        video.
      </li>
      <li>
        <strong>Takeaways become LinkedIn posts.</strong> The tactical
        framework from minute 34 of the episode becomes a five-bullet
        LinkedIn post that references the full episode.
      </li>
      <li>
        <strong>Strong lines become X posts.</strong> One-idea-per-post
        is the X ranking model. Your episode is the source library.
      </li>
      <li>
        <strong>Transcripts make quote extraction mechanical.</strong>{" "}
        Skimming a transcript is 10× faster than re-listening — see{" "}
        <Link to="/blog/podcast-transcript-seo" className="link">
          podcast transcript SEO
        </Link>{" "}
        for why the transcript is the substrate for every downstream
        asset.
      </li>
    </ul>
    <Figure
      src={IMG.transcript}
      alt="Full transcript with timestamps generated by EpisodeKit — the source material for extracting Shorts hooks, quotes, and social captions."
      caption="The transcript is the source library for every clip-worthy line, every quote, every social caption."
    />

    <h2 id="weekly-workflow">The weekly podcast social media workflow</h2>
    <p>
      End-to-end, one podcast episode to a full social distribution
      package, in nine steps.
    </p>
    <ol>
      <li>
        <strong>Record the episode.</strong> Any format. Quality
        matters here; downstream can&apos;t rescue it.
      </li>
      <li>
        <strong>Generate the transcript.</strong> Timestamped, with speaker labels. The source for everything social.
      </li>
      <li>
        <strong>Identify 5-10 strong moments.</strong> Scan for the
        patterns in the &quot;best moments&quot; section above.
        Timestamp each one.
      </li>
      <li>
        <strong>Create Shorts hooks.</strong> One or two hook variants
        per moment, tied to the specific payoff the clip delivers.
      </li>
      <li>
        <strong>Write platform-specific captions.</strong> TikTok,
        Reels, YouTube Shorts, LinkedIn, X — separate rewrites per
        surface.
      </li>
      <li>
        <strong>Extract quotes.</strong> Standalone lines for text-only
        posts, graphics, and Story cards.
      </li>
      <li>
        <strong>Schedule the social posts.</strong> Space the clips
        across the week the episode goes live — not all on launch day.
      </li>
      <li>
        <strong>Link back to the full episode, blog, or YouTube.</strong>{" "}
        Every clip is a doorway; the CTA is the long-form.
      </li>
      <li>
        <strong>Review what got engagement.</strong> Which hooks landed?
        Which platforms carried? Roll the learning into the next
        episode.
      </li>
    </ol>
    <p>
      Steps 2-6 collapse to a single automated pass when the content
      generation runs on a repeatable system — the architecture is in
      the{" "}
      <Link to="/blog/podcast-workflow-automation" className="link">
        podcast workflow automation guide
      </Link>
      . Human time per episode drops to review-and-approve.
    </p>
    <Figure
      src={IMG.showNotes}
      alt="Editorial show notes with summary, key takeaways, and chapter markers — the structural source for social content extraction."
      caption="Show notes flag the moments. Chapters mark the timestamps. Both feed the social workflow."
    />

    <h2 id="growth-system">How social clips fit into the full podcast growth system</h2>
    <p>
      Social distribution is one channel inside a five-channel growth
      system. Each channel does a different job:
    </p>
    <ul>
      <li>
        <strong>Google</strong> captures search intent — transcripts
        and blog posts ranking for long-tail and head-term queries.
        See{" "}
        <Link
          to="/blog/how-to-rank-your-podcast-on-google-2026"
          className="link"
        >
          how to rank your podcast on Google
        </Link>
        .
      </li>
      <li>
        <strong>YouTube</strong> expands search + recommendation
        discovery.
      </li>
      <li>
        <strong>Social</strong> creates repeated exposure — the topic
        of this article.
      </li>
      <li>
        <strong>Email</strong> keeps the audience warm between
        episodes — see{" "}
        <Link to="/blog/podcast-email-automation" className="link">
          podcast email automation
        </Link>
        .
      </li>
      <li>
        <strong>Website content</strong> compounds owned search
        traffic over time.
      </li>
    </ul>
    <p>
      Social clips don&apos;t replace long-form. They feed it. A viral
      45-second Short that links to the full episode is worth more
      than a well-shot episode with no distribution around it.
    </p>
    <Figure
      src={IMG.youtube}
      alt="YouTube description with hook, summary, key takeaways, chapter timestamps, CTA, and hashtags — the long-form destination that social clips point back to."
      caption="The YouTube description — the long-form destination social clips are engineered to drive traffic to."
    />

    <h2 id="mistakes">Common podcast clipping and repurposing mistakes</h2>
    <p>
      The failure modes are predictable. Audit your podcast social
      workflow against this list.
    </p>
    <ul>
      <li>
        <strong>Posting only &quot;new episode is live.&quot;</strong>{" "}
        Zero content value, zero reason to click. The default failure
        mode.
      </li>
      <li>
        <strong>Using the same caption everywhere.</strong> Every
        platform rewards different signals. Copy-paste underperforms
        native copy every time.
      </li>
      <li>
        <strong>Choosing boring clips.</strong> A well-produced clip of
        a mundane moment still won&apos;t travel. The clip has to earn
        the platform.
      </li>
      <li>
        <strong>No hook.</strong> First 2-3 seconds decide 90% of
        retention on short-form. A weak opening is a lost clip.
      </li>
      <li>
        <strong>No captions or text context.</strong> Sound-off default
        + no on-screen text = missed hook.
      </li>
      <li>
        <strong>No link back to the full episode.</strong> Every clip
        is a discovery surface; the CTA has to close the loop.
      </li>
      <li>
        <strong>No platform-specific angle.</strong> One caption
        shared across five surfaces is not repurposing — it&apos;s
        cross-posting.
      </li>
      <li>
        <strong>No consistency.</strong> Six clips this week, none for
        three weeks, four next Tuesday. Social platforms reward
        rhythm.
      </li>
      <li>
        <strong>Treating social as an afterthought.</strong> A
        show&apos;s social distribution is not a marketing chore. It
        is the top-of-funnel that determines whether the long-form
        gets seen.
      </li>
    </ul>

    <h2 id="episodekit-social">How EpisodeKit creates social-ready assets</h2>
    <p>
      <Link to="/" className="link">EpisodeKit</Link> is a podcast
      content distribution system — not a video editor, not a
      publishing tool. It generates the raw social-ready assets from
      one uploaded episode so the creator can move from the recording
      to a full social package in review time, not production time.
    </p>
    <p>Upload one episode. Get every social-adjacent asset:</p>
    <ul>
      <li>
        <strong>Transcript</strong> — the source library for every
        clip-worthy line and quote.
      </li>
      <li>
        <strong>Show notes</strong> — flags the strongest moments;
        chapters mark the timestamps to clip.
      </li>
      <li>
        <strong>Best quotes</strong> — pull-quotes ready for text
        posts, graphics, and Story cards.
      </li>
      <li>
        <strong>Shorts hooks</strong> — pattern-interrupt openings
        engineered for TikTok, Reels, and YouTube Shorts.
      </li>
      <li>
        <strong>Social captions</strong> — LinkedIn, X / Twitter,
        Instagram, TikTok — each platform-tuned rather than
        cross-posted.
      </li>
      <li>
        <strong>LinkedIn-style posts</strong> — insight-first
        long-caption format for the professional feed.
      </li>
      <li>
        <strong>X / Twitter posts</strong> — one-idea-per-post copy
        plus thread angles built from the episode&apos;s strongest
        lines.
      </li>
      <li>
        <strong>YouTube description</strong> — for the long-form
        destination social clips point back to.
      </li>
      <li>
        <strong>Discovery titles</strong> — variants structured for
        search-focused publishing on Google and YouTube surfaces.
      </li>
      <li>
        <strong>SEO blog post</strong> — the owned-domain URL
        LinkedIn and X posts link back to.
      </li>
    </ul>
    <p>
      EpisodeKit produces the copy and asset kit. Cutting the actual
      video clips and posting them lives in whichever tools you
      already use — see{" "}
      <Link to="/episode-examples/mfm-10k-to-1m" className="link">
        a real deliverable
      </Link>{" "}
      for the full output set or plans at{" "}
      <Link to="/pricing" className="link">/pricing</Link>.
    </p>
    <Figure
      src={IMG.discovery}
      alt="Discovery Titles output — surface-specific title and hook variants generated for Google and YouTube from a podcast episode."
      caption="Discovery titles — surface-tuned hook variants that pair with the Shorts and social captions."
    />

    <CTABlock
      title="See every social-ready asset on one real episode."
      body="Inspect the deliverable EpisodeKit generated from a 78-minute My First Million episode — transcript, quotes, Shorts hooks, discovery titles, social captions, and every other asset in the content system. All produced from one upload."
      href="/episode-examples/mfm-10k-to-1m"
      label="Inspect the deliverable"
    />

    <h2 id="final-thoughts">Final thoughts</h2>
    <p>
      Short-form content is not separate from podcasting. It&apos;s the
      discovery layer on top of it. Every episode contains multiple
      social angles — different clips for different segments, different
      captions per platform, different hooks per format. The shows
      that compound audience in 2026 aren&apos;t the ones grinding
      harder on individual TikToks; they&apos;re the ones running a
      repeatable weekly system that turns one recording into a
      surface-tuned social package.
    </p>
    <p>
      Consistency beats random posting. Repurposing beats production
      volume. Build the system once, run it every episode, and the
      short-form layer starts pulling audience back to the long-form
      within a few cycles.
    </p>

    <CTABlock
      title="Turn your next episode into a full social-ready asset kit."
      body="Upload one episode to EpisodeKit. Get the transcript, quotes, Shorts hooks, discovery titles, LinkedIn and X posts, Instagram / TikTok captions, and the SEO blog post — every social-ready asset your podcast distribution system needs, from one upload."
      href="/register"
      label="Try EpisodeKit free"
    />
  </>
);

const post = {
  slug: "podcast-to-social-media",
  title: "How to Turn Podcast Episodes Into Shorts, Reels, and TikToks in 2026",
  excerpt:
    "The repeatable weekly system for turning one podcast episode into surface-tuned Shorts hooks, TikTok and Reels captions, LinkedIn posts, X quotes, and short-form discovery assets — without cross-posting.",
  category: "Podcast Social",
  readingTime: "13 min read",
  publishedAt: "2026-07-16",
  dateModified: "2026-09-08",
  author: {
    name: "The EpisodeKit Team",
    role: "Distribution research",
  },
  seo: {
    title:
      "How to Turn Podcast Episodes Into Shorts, Reels, and TikToks in 2026 | EpisodeKit",
    description:
      "Learn how to repurpose podcast episodes into Shorts, Reels, TikToks, hooks, captions, quotes, and social posts using one recording as the source for every platform.",
    ogImage: "/examples/mfm-10k-to-1m/previews/cover-large.jpg",
  },
  cover: {
    src: IMG.cover,
    alt: "EpisodeKit deliverable cover — one uploaded episode turned into a surface-tuned social distribution package.",
  },
  toc: [
    { id: "clips-matter", label: "Why podcast clips matter in 2026" },
    { id: "what-it-means", label: "What 'podcast to social media' really means" },
    { id: "multiple-assets", label: "Why one episode should create multiple short-form assets" },
    { id: "best-moments", label: "The best podcast moments to turn into clips" },
    { id: "hooks", label: "How to write Shorts hooks from podcast episodes" },
    { id: "platform-adaptation", label: "How to adapt clips for TikTok, Reels, Shorts, and LinkedIn" },
    { id: "captions-quotes", label: "How captions and quotes support social distribution" },
    { id: "weekly-workflow", label: "The weekly podcast social media workflow" },
    { id: "growth-system", label: "How social clips fit into the full podcast growth system" },
    { id: "mistakes", label: "Common podcast clipping and repurposing mistakes" },
    { id: "episodekit-social", label: "How EpisodeKit creates social-ready assets" },
    { id: "final-thoughts", label: "Final thoughts" },
  ],
  Body,
};

export default post;
